/**
 * Single sign-on for the admin console, against whichever identity provider the deployment uses.
 *
 * The two providers work differently on purpose:
 *
 * - **Entra** signs in from the browser with MSAL (auth-code + PKCE against the SPA registration)
 *   and presents the access token as a bearer on every request.
 * - **Google** signs in through the *backend*: `signIn()` navigates to the server's
 *   `/api/auth/google/login`, which runs the whole OAuth flow server-side and answers with an
 *   httpOnly session cookie (`backend/web_login.py`). No token ever reaches this code, which is
 *   why `getSsoToken()` returns null for Google — the cookie rides along on every same-origin
 *   request by itself. The scopes Google is asked for are therefore backend configuration, not a
 *   frontend build argument.
 *
 * Everything the rest of the app needs is `signIn()` and `getSsoToken()`. `client.ts` calls the
 * latter on every request; nothing else in the app knows an identity provider exists.
 *
 * Configure at build time:
 *   VITE_IDP            "entra" | "google" | "" (unset disables SSO, leaving the break-glass form)
 *   VITE_OAUTH_CLIENT_ID    Entra only: the SPA app registration id
 *   VITE_OAUTH_AUTHORITY    Entra only, e.g. https://login.microsoftonline.com/<tenant>
 *   VITE_OAUTH_SCOPES       Entra only: space-separated scopes to request
 */

import { PublicClientApplication } from "@azure/msal-browser";
import type { AccountInfo, SilentRequest } from "@azure/msal-browser";
import { API_BASE_URL } from "./baseUrl";

export type Idp = "entra" | "google" | "";

const IDP = (import.meta.env.VITE_IDP ?? "") as Idp;
const CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID ?? "";
const AUTHORITY = import.meta.env.VITE_OAUTH_AUTHORITY ?? "";
const SCOPES = (import.meta.env.VITE_OAUTH_SCOPES ?? "").split(" ").filter(Boolean);

/** Where the backend's Google sign-in flow starts. Navigated to, never fetched. */
const GOOGLE_LOGIN_URL = `${API_BASE_URL}/api/auth/google/login`;

/**
 * Which provider this build signs in against, or "" when SSO was not configured.
 * Google needs no client id here — the backend holds the OAuth client — so `VITE_IDP=google`
 * alone enables it; Entra still requires the SPA registration's id.
 */
export function configuredIdp(): Idp {
  if (IDP === "google") {
    return IDP;
  }
  return CLIENT_ID ? IDP : "";
}

/** Human-readable provider name, for the sign-in button. */
export function idpLabel(): string {
  return configuredIdp() === "google" ? "Google" : "Microsoft";
}

// --- Entra ------------------------------------------------------------------------------

let msal: PublicClientApplication | null = null;

async function entraClient(): Promise<PublicClientApplication> {
  if (!msal) {
    msal = new PublicClientApplication({
      auth: { clientId: CLIENT_ID, authority: AUTHORITY, redirectUri: window.location.origin },
      // sessionStorage rather than localStorage: the token should not outlive the tab, matching
      // how `authToken.ts` treats the break-glass session.
      cache: { cacheLocation: "sessionStorage" },
    });
    await msal.initialize();
  }
  return msal;
}

function firstAccount(client: PublicClientApplication): AccountInfo | null {
  return client.getAllAccounts()[0] ?? null;
}

async function entraSignIn(): Promise<void> {
  const client = await entraClient();
  await client.loginPopup({ scopes: SCOPES });
}

async function entraToken(): Promise<string | null> {
  const client = await entraClient();
  const account = firstAccount(client);
  if (!account) {
    return null;
  }
  const request: SilentRequest = { scopes: SCOPES, account };
  try {
    return (await client.acquireTokenSilent(request)).accessToken;
  } catch {
    // Silent renewal fails routinely — the refresh token expired, or consent was revoked. It is
    // not an error to report; it means "sign in again", which the caller handles by getting null.
    return null;
  }
}

async function entraSignOut(): Promise<void> {
  const client = await entraClient();
  const account = firstAccount(client);
  if (account) {
    await client.clearCache({ account });
  }
}

// --- Google -----------------------------------------------------------------------------

function googleSignIn(): Promise<void> {
  // A full-page hand-off: the backend redirects to Google and back, then lands on "/" with the
  // session cookie set. This promise never settles — the navigation unloads the page.
  window.location.assign(GOOGLE_LOGIN_URL);
  return new Promise<void>(() => {});
}

// --- The seam the rest of the app uses --------------------------------------------------

/**
 * Run the interactive sign-in flow. For Entra the returned promise resolves once a token is
 * held; for Google it never settles, because sign-in is a navigation away from this page.
 */
export async function signIn(): Promise<void> {
  return configuredIdp() === "google" ? googleSignIn() : entraSignIn();
}

/**
 * The current SSO bearer token, renewed silently where the provider supports it, or null when
 * there is none to attach. Null is the *normal* state for Google — its credential is an httpOnly
 * cookie the browser sends by itself. Never throws.
 */
export async function getSsoToken(): Promise<string | null> {
  return configuredIdp() === "entra" ? entraToken() : null;
}

/**
 * Whether the session may exist server-side even though no token is held here — true for the
 * Google cookie flow, where only asking `/api/session` can tell. `AuthContext` uses this to
 * decide whether a session check is worth making on page load.
 */
export function sessionIsCookieBased(): boolean {
  return configuredIdp() === "google";
}

/** Clear provider-side state, so the next sign-in is a real one. Google's session lives in the
 * backend cookie, which `client.ts`'s `signOutServer` clears — nothing is held here. */
export async function signOut(): Promise<void> {
  if (configuredIdp() !== "entra") {
    return;
  }
  try {
    await entraSignOut();
  } catch {
    // Recover silently: the local session is cleared regardless by `authToken.clearToken`, and a
    // provider that will not co-operate must not leave the user stuck on a half-signed-out screen.
  }
}
